import axios, { AxiosRequestConfig } from 'axios';
import { AxiosInstance } from 'axios';
import * as loglevel from 'loglevel';

import { IApiConnector, TRequestFields, TLoginParams, TRequestPwdlessCodeParams } from '../types';

import AccessToken from './AccessToken';
import OauthClient from './OauthClient';
import User from './User';

export default class ApiConnector {
    config: IApiConnector.Config;
    oauthClient: OauthClient;
    logger: loglevel.Logger;
    user: User;
    refreshTokenPromise: Promise<any>;
    validityPollInterval: NodeJS.Timer;

    constructor(config: IApiConnector.Config, oauthClient: OauthClient, logger?: loglevel.Logger) {
        this.config = config;
        this.oauthClient = oauthClient;
        this.logger = logger;
    }

    getHttpClient(service: string, includeToken: boolean = true): AxiosInstance {
        if (includeToken) {
            if (!this.oauthClient.hasAccessToken()) {
                this.logger && this.logger.warn(`OauthClient has empty access token`);
            } else if (!this.oauthClient.hasValidAccessToken()) {
                this.logger && this.logger.warn(`OauthClient Access token has expired. Refresh needed...`);
            }
        }

        const client = axios.create({ ...(this.config.services[service] ?? {}), withCredentials: false });

        if (includeToken === true) {
            const injectToken = (config: AxiosRequestConfig) => {
                config.headers.Authorization = `Bearer ${this.oauthClient.getAccessTokenJwt()}`;
                return config;
            };

            client.interceptors.request.use(
                (config) => {
                    if (this.oauthClient.hasAccessToken()) {
                        if (!this.oauthClient.hasValidAccessToken()) {
                            return new Promise((resolve, reject) => {
                                this.refreshAccessToken()
                                    .then(() => {
                                        resolve(injectToken(config));
                                    })
                                    .catch((error) => {
                                        this.logger.error(error);
                                        resolve(config);
                                    });
                            });
                        }
                        return injectToken(config);
                    }
                    return config;
                },
                (error) => {
                    this.logger.error(error);
                    return Promise.reject(error);
                }
            );

            client.interceptors.response.use((response) => {
                this.startTokenValidityPoll();
                return response;
            });
        }

        return client;
    }

    getUser(): User {
        return this.user;
    }

    isLoggedIn(): boolean {
        return this.oauthClient.hasValidAccessToken() && !!this.user.id;
    }

    requestPasswordlessLoginCode({ channel, recipient }: TRequestPwdlessCodeParams): Promise<any> {
        if (!this.config.auth) {
            throw 'ApiConnector auth is not configured';
        }

        // TODO: more granular config validate

        return this.getHttpClient(this.config.auth.service, false).post(this.config.auth.passwordlessRequestCode, {
            client_id: this.oauthClient.config.client_id,
            channel,
            recipient,
        });
    }

    async login({ grant_type, code, username, password }: TLoginParams): Promise<AccessToken> {
        this.logger && this.logger.debug(`ApiConnector - login`);

        if (!this.config.auth) {
            throw 'ApiConnector auth is not configured';
        }

        // TODO: more granular config validate

        try {
            const response = await this.getHttpClient(this.config.auth.service, false).post(this.config.auth.login, {
                grant_type,
                code,
                username,
                password,
            });
            const accessToken = new AccessToken(response.data);
            this.oauthClient.setAccessToken(accessToken);
            return accessToken;
        } catch (error) {
            this.logger.error(error);
            throw error;
        }
    }

    async logout(): Promise<any> {
        this.logger && this.logger.debug(`ApiConnector - logout`);

        if (!this.config.auth) {
            throw 'ApiConnector auth is not configured';
        }

        try {
            this.user = null;
            const response = await this.getHttpClient(this.config.auth.service).post(this.config.auth.logout);
            return response.data;
        } catch (error) {
            this.logger.error(error);
        } finally {
            this.oauthClient.clearAccessToken();
        }
    }

    async whoAmI(params?: TRequestFields): Promise<any> {
        this.logger && this.logger.debug(`ApiConnector - WhoAmI`);

        if (!this.config.auth) {
            throw 'ApiConnector auth is not configured';
        }

        // TODO: more granular config validate

        try {
            const response = await this.getHttpClient(this.config.auth.service).get(this.config.auth.whoAmI, {
                params,
            });

            if (!response.data || !response.data.id) {
                throw new Error('no_user_id_in_response');
            }

            this.user = new User(response.data);
            return response.data;
        } catch (error) {
            this.logger.error(error);
        }
    }

    refreshAccessToken(): Promise<any> {
        if (!this.config.auth) {
            throw 'ApiConnector auth is not configured';
        }

        // TODO: more granular config validate

        if (this.refreshTokenPromise) {
            this.logger && this.logger.debug(`ApiConnector - Refresh token in progress, returning promise`);
            return this.refreshTokenPromise;
        }

        this.logger && this.logger.debug(`ApiConnector - Refreshing access token...`);

        return (this.refreshTokenPromise = new Promise((resolve, reject) => {
            const accessToken = this.oauthClient.getAccessToken();

            // TODO: causes problems with leeway
            // if (accessToken.isValid()) {
            //     return reject('access_token_still_valid');
            // }

            const [client_id, user_id, refresh_token] = [
                accessToken.getClientId(),
                accessToken.getResourceOwnerId(),
                accessToken.getRefreshToken(),
            ];

            if (!client_id) {
                return reject('no_client_id');
            }
            if (!user_id) {
                return reject('no_user_id');
            }
            if (!refresh_token) {
                return reject('no_refresh_token');
            }

            this.getHttpClient(this.config.auth.service, false)
                .post(this.config.auth.refreshToken, { client_id, user_id, refresh_token })
                .then((response) => {
                    const accessToken = new AccessToken(response.data);
                    this.oauthClient.setAccessToken(accessToken);
                    this.logger && this.logger.debug('Refreshing token success');
                    resolve(this.oauthClient.getAccessToken());
                })
                .catch((error) => {
                    this.oauthClient.clearAccessToken();
                    this.logger && this.logger.error(error);
                    reject((error.response && error.response.data) || error);
                });
        }).finally(() => {
            this.refreshTokenPromise = null;
        }));
    }

    private startTokenValidityPoll(intervalSeconds = 60): NodeJS.Timer {
        if (intervalSeconds <= 0) {
            return;
        }
        const check = () => {
            if (this.oauthClient.hasAccessToken() && !this.oauthClient.hasValidAccessToken()) {
                this.refreshAccessToken();
            }
        };
        this.stopTokenValidityPoll();
        this.validityPollInterval = setInterval(check, intervalSeconds * 1000);
        return this.validityPollInterval;
    }

    private stopTokenValidityPoll() {
        if (this.validityPollInterval) {
            clearInterval(this.validityPollInterval);
        }
        this.validityPollInterval = null;
    }
}
